import React from 'react'
import classNames from 'classnames'
import { setCookie } from 'cookies-next'
import { v4 as uuid } from 'uuid'
import { LinkButton } from '@/atoms/Button'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { paths } from '@/constants/paths'
import { PIF_NAME_IN_CREDITS_MIN_TICKETS } from '@/constants/pif'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { getPeopleImpacted, Offer } from '@/services/PifService'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'

interface TheatricalPifCheckoutWithSubtotalProps {
  projectSlug: Maybe<string>
  selectedOffer: Offer | undefined
  isSubscription?: boolean
  newPifLayout?: boolean
  pifNameInCreditsEnabled?: boolean
  overlayStyle?: boolean
}

export const TheatricalPifCheckoutWithSubtotal: React.FC<TheatricalPifCheckoutWithSubtotalProps> = ({
  selectedOffer,
  projectSlug,
  isSubscription,
  newPifLayout,
  pifNameInCreditsEnabled = false,
  overlayStyle,
}) => {
  const track = useSafeTrack()
  const { trackProductAdded } = useEcommerceEvents()
  const { t } = useTranslate('pif')
  const { nameInCredits } = usePayItForwardOfferData()

  if (!selectedOffer) return null

  const { id, slug, name, price, currency, region } = selectedOffer
  // const ticketCount = getPeopleImpacted(selectedOffer)
  const formattedPrice = formatPrice(price, { includeDecimals: false, currency })

  const trackCheckoutStarted = ({ isSubscription }: { isSubscription: boolean }) => {
    const checkoutId = uuid()
    setCookie('pif_checkout_id', checkoutId)

    const checkoutStartedPayload = {
      category: 'pif',
      project: projectSlug,
      products: [
        {
          product_id: id,
          sku: slug,
          category: 'pif',
          name: name,
          price: price,
          currency: currency,
          region: region,
          quantity: 1,
          url: `${paths.getBaseURL()}${paths.payItForward.index}/${projectSlug}/checkout/${id}`,
        },
      ],
      subscription: isSubscription,
      currency: currency,
      region: region,
      tax: 0,
      shipping: 0,
      potential_revenue: getPriceForCurrency(price, currency),
      checkout_id: checkoutId,
    }
    // TODO: deprecated - remove after release of trackProductAdded and marketing's (Kylo's) blessing
    track('Checkout Started', checkoutStartedPayload)

    trackProductAdded({
      funnel: 'pif',
      product_id: id,
      sku: slug,
      category: 'pif',
      name,
      price: getPriceForCurrency(price, currency),
      currency: currency as MoneyCurrencyCode,
      quantity: 1,
      url: `${paths.getBaseURL()}${paths.payItForward.index}/${projectSlug}/checkout/${id}`,
      projectSlug,
    })
  }

  // Disable the CTA if the Name in Credits field is shown and invalid
  const ticketCount = getPeopleImpacted(selectedOffer)
  const isNameInCreditsShown =
    pifNameInCreditsEnabled && Boolean(newPifLayout) && ticketCount >= PIF_NAME_IN_CREDITS_MIN_TICKETS
  const nic = (nameInCredits ?? '').trim()
  const isNameInCreditsInvalid = nic.length < 3 || nic.length > 40
  const shouldDisableCta = isNameInCreditsShown && isNameInCreditsInvalid

  // Include nameInCredits in the checkout URL when valid
  const baseOfferPath = `${paths.payItForward.index}/${projectSlug}/checkout/${id}`
  const offerPath =
    isNameInCreditsShown && !isNameInCreditsInvalid
      ? `${baseOfferPath}?nameInCredits=${encodeURIComponent(nic)}`
      : baseOfferPath

  return (
    <div
      className={classNames('mt-4 flex w-full flex-col gap-6 pt-3 md:mt-6 lg:flex-row-reverse lg:justify-between', {
        'border-t-[1px] border-core-gray-300': !newPifLayout,
      })}
    >
      {/* <div className="flex shrink-0 flex-col gap-1 text-right">
        <Text className="photon-title-xs md:photon-title-sm font-medium">
          {t('theatricalSubTotal', 'Subtotal')}: <span className="font-bold">{formattedPrice}</span>
        </Text>
        <CaptionSM weight="medium" color="core-gray-700">
          {t('theatricalGiftThisManyTickets', 'Gift {{numTickets}} tickets', { numTickets: ticketCount })}
        </CaptionSM>
      </div> */}

      <LinkButton
        href={offerPath}
        variant="oxide-primary"
        size="md"
        className={classNames(
          'flex w-full items-center justify-between gap-6 !rounded-xl py-3 md:mr-auto md:w-fit',
          {
            'opacity-50': shouldDisableCta,
          },
          overlayStyle &&
            'bg-david-orange border-david-orange text-white hover:border-core-warning-500 hover:bg-core-warning-500 active:border-core-warning-700 active:bg-core-warning-700',
        )}
        disabled={shouldDisableCta}
        onClick={() => {
          trackCheckoutStarted({ isSubscription: isSubscription || false })
        }}
      >
        <span className="flex items-center gap-1.5 text-white">
          <PayItForwardOutlineIcon className="shrink-0" color="white" size={20} />
          {t('payItForward', 'Pay it Forward')}
        </span>
        <span className="flex items-center text-white/80">
          {formattedPrice}
          <CaretRightIcon color="white" size={24} />
        </span>
      </LinkButton>
    </div>
  )
}
